import React, { useState, useRef, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

// Icons
import NotificationsIcon from '@mui/icons-material/Notifications';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArticleIcon from '@mui/icons-material/Article';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

const NotificationBell = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();

  const [anchorEl, setAnchorEl] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(
    localStorage.getItem('notificationSound') !== 'disabled'
  );

  // Ref for notification sound
  const notificationSoundRef = useRef(new Audio('/sounds/notification.mp3'));

  // Track previous unread count
  const previousUnreadCount = useRef(unreadCount);

  // Detect new notifications
  useEffect(() => {
    // If unread count increased, we got a new notification
    if (unreadCount > previousUnreadCount.current) {
      playNotificationSound();
    }

    // Update the ref for next comparison
    previousUnreadCount.current = unreadCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  const playNotificationSound = () => {
    if (soundEnabled) {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch(err => {
        console.warn('Could not play notification sound:', err);
      });
    }
  };

  // Notification type icon mapping
  const notificationIcons = {
    priceAlert: <TrendingUpIcon fontSize="small" color="success" />,
    newsAlert: <ArticleIcon fontSize="small" color="primary" />,
    congressTrade: <AccountBalanceIcon fontSize="small" color="secondary" />,
    insiderTrade: <BusinessIcon fontSize="small" color="warning" />,
    marketEvent: <EventIcon fontSize="small" color="error" />
  };

  const recentNotifications = notifications
    .filter(n => !n.isRead)  // Only unread notifications
    .slice(0, 10);           // Take first 10

  // Handle opening the notification menu
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing the notification menu
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Toggle notification sound
  const toggleSound = (event) => {
    event.stopPropagation();
    const newSetting = !soundEnabled;
    setSoundEnabled(newSetting);
    localStorage.setItem('notificationSound', newSetting ? 'enabled' : 'disabled');
  };

  // Handle marking all as read
  const handleMarkAllAsRead = async (event) => {
    event.stopPropagation();
    await markAllAsRead();
  };

  // Handle clicking on a notification
  const handleNotificationClick = (notification) => {
    handleMenuClose();

    // If not read, mark as read
    if (!notification.isRead) {
      markAsRead(notification._id);
    }

    // Navigate to notifications page
    navigate('/alerts', { state: { initialTab: 1 } });
  };

  // Handle view all click
  const handleViewAll = () => {
    handleMenuClose();
    navigate('/alerts', { state: { initialTab: 1 } });
  };

  // Format notification date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    // If today, show time
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit'
      });
    }

    // If yesterday, show "Yesterday"
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }

    // Otherwise show date
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="notifications"
        onClick={handleMenuOpen}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 500,
            overflow: 'auto'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Notifications
            {unreadCount > 0 && ` (${unreadCount})`}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={soundEnabled ? "Mute notifications" : "Unmute notifications"}>
              <IconButton size="small" onClick={toggleSound}>
                {soundEnabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllAsRead}
              >
                Mark All Read
              </Button>
            )}
          </Box>
        </Box>

        <Divider />

        {recentNotifications.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <>
            {recentNotifications.map((notification) => (
              <MenuItem
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  py: 1.5,
                  px: 2,
                  borderLeft: notification.isRead ? 'none' : '4px solid',
                  borderLeftColor: 'primary.main',
                  bgcolor: notification.isRead ? 'transparent' : 'action.hover'
                }}
              >
                <ListItemIcon>
                  {notificationIcons[notification.type] || <NotificationsIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" noWrap>
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '180px' }}>
                        {notification.content}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(notification.createdAt)}
                      </Typography>
                    </Box>
                  }
                />
              </MenuItem>
            ))}
          </>
        )}

        <Divider />

        <MenuItem onClick={handleViewAll}>
          <ListItemText
            primary="View Alerts & Notifications"
            sx={{ textAlign: 'center' }}
          />
          <ArrowForwardIcon fontSize="small" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default NotificationBell;